import { z } from 'zod';

import { CashMovementDtoSchema } from './cash-movement.schema.js';
import { CashDenominationSchema } from './cash-sessions.schema.js';
import { CashModeScopeTypeSchema } from './cash-operating-mode.schema.js';
import {
  cashVarianceActionStatusValues,
  cashVarianceActionTypeValues,
  cashVarianceCaseStatusValues,
  cashVarianceOverageClassificationValues,
  cashVarianceRecoveryDirectionValues,
  cashVarianceStageValues,
  type ApplyCashVarianceWriteoffRequest,
  type ApplyCashVarianceWriteoffResponse,
  type ApplyCashVarianceOffsetRequest,
  type ApplyCashVarianceOffsetResponse,
  type ApproveCashVarianceRecoveryRequest,
  type ApproveCashVarianceRecoveryResponse,
  type CashVarianceActionDto,
  type CashVarianceDenominationCaptureDto,
  type CashVarianceActionStatus,
  type CashVarianceActionType,
  type CashVarianceCaseDto,
  type CashVarianceCaseStatus,
  type CashVarianceOverageClassification,
  type CashVarianceRecoveryDirection,
  type CashVarianceStage,
  type ClassifyCashVarianceOverageRequest,
  type ClassifyCashVarianceOverageResponse,
  type GetCashVarianceCaseRequest,
  type GetCashVarianceCaseResponse,
  type ListCashVarianceCasesRequest,
  type ListCashVarianceCasesResponse,
  type RemoveCashVarianceOverageRequest,
  type RemoveCashVarianceOverageResponse,
  type RejectCashVarianceRecoveryRequest,
  type RejectCashVarianceRecoveryResponse,
  type SubmitCashVarianceRecoveryRequest,
  type SubmitCashVarianceRecoveryResponse,
} from './cash-variance.dto.js';

const timestampSchema = z.iso.datetime();

export const CashVarianceStageSchema: z.ZodType<CashVarianceStage> =
  z.enum(cashVarianceStageValues);
export const CashVarianceCaseStatusSchema: z.ZodType<CashVarianceCaseStatus> = z.enum(
  cashVarianceCaseStatusValues,
);
export const CashVarianceActionTypeSchema: z.ZodType<CashVarianceActionType> = z.enum(
  cashVarianceActionTypeValues,
);
export const CashVarianceActionStatusSchema: z.ZodType<CashVarianceActionStatus> = z.enum(
  cashVarianceActionStatusValues,
);
export const CashVarianceRecoveryDirectionSchema: z.ZodType<CashVarianceRecoveryDirection> = z.enum(
  cashVarianceRecoveryDirectionValues,
);
export const CashVarianceOverageClassificationSchema: z.ZodType<CashVarianceOverageClassification> =
  z.enum(cashVarianceOverageClassificationValues);

export const CashVarianceDenominationCaptureDtoSchema: z.ZodType<CashVarianceDenominationCaptureDto> =
  z
    .object({
      version: z.literal(1),
      effectiveMode: z.literal('denomination_inventory'),
      scopeType: CashModeScopeTypeSchema,
      scopeId: z.uuid(),
      scopeModeStateId: z.uuid(),
      configurationRevision: z.number().int().positive(),
      entitlementRevision: z.number().int().nonnegative(),
    })
    .strict();

export const CashVarianceCaseDtoSchema: z.ZodType<CashVarianceCaseDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  cashLocationId: z.uuid(),
  cashSessionId: z.uuid(),
  stage: CashVarianceStageSchema,
  initialAmountMinor: z.number().int(),
  outstandingAmountMinor: z.number().int(),
  recoveredAmountMinor: z.number().int().nonnegative(),
  writtenOffAmountMinor: z.number().int().nonnegative(),
  status: CashVarianceCaseStatusSchema,
  openedByUserId: z.uuid(),
  openedAt: timestampSchema,
  resolvedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const CashVarianceActionDtoSchema: z.ZodType<CashVarianceActionDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    caseId: z.uuid(),
    actionType: CashVarianceActionTypeSchema,
    status: CashVarianceActionStatusSchema,
    amountMinor: z.number().int(),
    recoveryDirection: CashVarianceRecoveryDirectionSchema.nullable(),
    denominations: z.array(CashDenominationSchema).nullable(),
    denominationCapture: CashVarianceDenominationCaptureDtoSchema.nullable().optional(),
    note: z.string().trim().min(1).max(500).nullable(),
    submissionActionId: z.uuid().nullable(),
    targetCashLocationId: z.uuid().nullable(),
    targetCashSessionId: z.uuid().nullable(),
    linkedMovementId: z.uuid().nullable(),
    relatedCaseId: z.uuid().nullable().optional(),
    relatedActionId: z.uuid().nullable().optional(),
    classification: CashVarianceOverageClassificationSchema.nullable().optional(),
    submittedByUserId: z.uuid(),
    reviewedByUserId: z.uuid().nullable(),
    submittedAt: timestampSchema,
    reviewedAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .superRefine((value, context) => {
    if (
      value.denominationCapture != null &&
      (value.actionType !== 'recovery_submitted' || value.denominations === null)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Denomination capture metadata is valid only for a rowful recovery submission.',
        path: ['denominationCapture'],
      });
    }

    if (value.actionType === 'offset_applied' && !value.relatedCaseId) {
      context.addIssue({
        code: 'custom',
        message: 'Offset actions must link the related shortage or overage case.',
        path: ['relatedCaseId'],
      });
    }

    if (value.actionType === 'overage_classified' && !value.classification) {
      context.addIssue({
        code: 'custom',
        message: 'Overage classification actions must include a classification.',
        path: ['classification'],
      });
    }

    if (value.actionType === 'overage_removed') {
      if (!value.targetCashLocationId) {
        context.addIssue({
          code: 'custom',
          message: 'Overage removal actions must include a target cash location.',
          path: ['targetCashLocationId'],
        });
      }
    }
  });

export const ListCashVarianceCasesRequestSchema: z.ZodType<ListCashVarianceCasesRequest> = z.object(
  {
    companyId: z.uuid(),
    status: CashVarianceCaseStatusSchema.optional(),
    cashLocationId: z.uuid().optional(),
    stationId: z.uuid().optional(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    from: timestampSchema.optional(),
    to: timestampSchema.optional(),
  },
);

export const ListCashVarianceCasesResponseSchema: z.ZodType<ListCashVarianceCasesResponse> =
  z.object({
    items: z.array(CashVarianceCaseDtoSchema),
  });

export const GetCashVarianceCaseRequestSchema: z.ZodType<GetCashVarianceCaseRequest> = z.object({
  companyId: z.uuid(),
  caseId: z.uuid(),
});

export const GetCashVarianceCaseResponseSchema: z.ZodType<GetCashVarianceCaseResponse> = z.object({
  cashVarianceCase: CashVarianceCaseDtoSchema,
  actions: z.array(CashVarianceActionDtoSchema),
  linkedMovementIds: z.array(z.uuid()),
});

export const SubmitCashVarianceRecoveryRequestSchema: z.ZodType<SubmitCashVarianceRecoveryRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema,
    caseId: z.uuid(),
    direction: CashVarianceRecoveryDirectionSchema,
    amountMinor: z.number().int().positive(),
    denominations: z.array(CashDenominationSchema).nullable().optional(),
    note: z.string().trim().min(1).max(500).nullable().optional(),
  });

export const SubmitCashVarianceRecoveryResponseSchema: z.ZodType<SubmitCashVarianceRecoveryResponse> =
  z.object({
    cashVarianceCase: CashVarianceCaseDtoSchema,
    action: CashVarianceActionDtoSchema,
  });

export const ApproveCashVarianceRecoveryRequestSchema: z.ZodType<ApproveCashVarianceRecoveryRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema,
    actionId: z.uuid(),
    targetCashLocationId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    stationId: z.uuid().optional(),
  });

export const ApproveCashVarianceRecoveryResponseSchema: z.ZodType<ApproveCashVarianceRecoveryResponse> =
  z.object({
    cashVarianceCase: CashVarianceCaseDtoSchema,
    action: CashVarianceActionDtoSchema,
    movement: CashMovementDtoSchema,
  });

export const RejectCashVarianceRecoveryRequestSchema: z.ZodType<RejectCashVarianceRecoveryRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema,
    actionId: z.uuid(),
    note: z.string().trim().min(1).max(500).nullable().optional(),
  });

export const RejectCashVarianceRecoveryResponseSchema: z.ZodType<RejectCashVarianceRecoveryResponse> =
  z.object({
    cashVarianceCase: CashVarianceCaseDtoSchema,
    action: CashVarianceActionDtoSchema,
  });

export const ApplyCashVarianceWriteoffRequestSchema: z.ZodType<ApplyCashVarianceWriteoffRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema,
    caseId: z.uuid(),
    amountMinor: z.number().int().positive(),
    note: z.string().trim().min(1).max(500),
  });

export const ApplyCashVarianceWriteoffResponseSchema: z.ZodType<ApplyCashVarianceWriteoffResponse> =
  z.object({
    cashVarianceCase: CashVarianceCaseDtoSchema,
    action: CashVarianceActionDtoSchema,
  });

export const ApplyCashVarianceOffsetRequestSchema: z.ZodType<ApplyCashVarianceOffsetRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema,
    shortageCaseId: z.uuid(),
    overageCaseId: z.uuid(),
    amountMinor: z.number().int().positive(),
    note: z.string().trim().min(1).max(500),
  })
  .refine((value) => value.shortageCaseId !== value.overageCaseId, {
    message: 'Shortage and overage cases must be different cases.',
    path: ['overageCaseId'],
  });

export const ApplyCashVarianceOffsetResponseSchema: z.ZodType<ApplyCashVarianceOffsetResponse> =
  z.object({
    shortageCase: CashVarianceCaseDtoSchema,
    overageCase: CashVarianceCaseDtoSchema,
    shortageAction: CashVarianceActionDtoSchema,
    overageAction: CashVarianceActionDtoSchema,
  });

export const ClassifyCashVarianceOverageRequestSchema: z.ZodType<ClassifyCashVarianceOverageRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema,
    caseId: z.uuid(),
    amountMinor: z.number().int().positive(),
    classification: CashVarianceOverageClassificationSchema,
    note: z.string().trim().min(1).max(500),
  });

export const ClassifyCashVarianceOverageResponseSchema: z.ZodType<ClassifyCashVarianceOverageResponse> =
  z.object({
    cashVarianceCase: CashVarianceCaseDtoSchema,
    action: CashVarianceActionDtoSchema,
  });

export const RemoveCashVarianceOverageRequestSchema: z.ZodType<RemoveCashVarianceOverageRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    occurredAt: timestampSchema,
    caseId: z.uuid(),
    amountMinor: z.number().int().positive(),
    targetCashLocationId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    stationId: z.uuid().optional(),
    denominations: z.array(CashDenominationSchema).nullable().optional(),
    note: z.string().trim().min(1).max(500),
  });

export const RemoveCashVarianceOverageResponseSchema: z.ZodType<RemoveCashVarianceOverageResponse> =
  z.object({
    cashVarianceCase: CashVarianceCaseDtoSchema,
    action: CashVarianceActionDtoSchema,
    movement: CashMovementDtoSchema,
  });
